import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

type SetAccountSyncing = (accountId: string, syncing: boolean) => void;

const EMPTY_IDS: Set<string> = new Set();
const NOOP: SetAccountSyncing = () => {};

// Deux contextes distincts, volontairement :
//
// L'état (le Set) change à chaque début/fin de synchro. Si le setter vivait
// dans le même contexte, tout composant qui veut seulement ÉCRIRE (les boutons
// "Actualiser") se réabonnerait aussi aux changements de valeur et se
// re-rendrait à chaque transition — y compris celle qu'il vient de déclencher.
// C'est ce qui figeait le spinner du bouton Actualiser du journal : la page se
// re-rendait en cascade au moment même où isRefreshing passait à true.
//
// En séparant, les écrivains consomment une référence stable (useCallback avec
// deps vides) et ne se re-rendent jamais à cause de la synchro ; seul le
// sélecteur de compte, qui lit réellement le Set, se met à jour.
const AccountSyncStateContext = createContext<Set<string>>(EMPTY_IDS);
const AccountSyncSetterContext = createContext<SetAccountSyncing>(NOOP);

export function AccountSyncProvider({ children }: { children: ReactNode }) {
  const [syncingAccountIds, setSyncingAccountIds] = useState<Set<string>>(EMPTY_IDS);

  const setAccountSyncing = useCallback<SetAccountSyncing>((accountId, syncing) => {
    setSyncingAccountIds((prev) => {
      if (syncing === prev.has(accountId)) return prev;

      const next = new Set(prev);
      if (syncing) {
        next.add(accountId);
      } else {
        next.delete(accountId);
      }
      return next;
    });
  }, []);

  return (
    <AccountSyncSetterContext.Provider value={setAccountSyncing}>
      <AccountSyncStateContext.Provider value={syncingAccountIds}>
        {children}
      </AccountSyncStateContext.Provider>
    </AccountSyncSetterContext.Provider>
  );
}

/** Comptes dont une synchro broker est en cours. À n'utiliser que pour l'affichage. */
export function useSyncingAccountIds(): Set<string> {
  return useContext(AccountSyncStateContext);
}

/** Référence stable : n'entraîne aucun re-render chez celui qui l'appelle. */
export function useSetAccountSyncing(): SetAccountSyncing {
  return useContext(AccountSyncSetterContext);
}
